import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { TableModule } from 'primeng/table';
import { ImageModule } from 'primeng/image';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { CalendarModule } from 'primeng/calendar';
import { ToggleButtonModule } from 'primeng/togglebutton';
import {
  FormsModule,
  ReactiveFormsModule,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { InputNumberModule } from 'primeng/inputnumber';
import { FileUploadModule } from 'primeng/fileupload';
import { ToastModule } from 'primeng/toast';
import { CoreApiService } from '../../../../core/service/core-api-service';
import { AppService } from '../../../../core/service/app.service';
import { properties } from '../../../../../environments/properties';
import { StoreModel } from '../../../../model/store.model';
import { StoreScheduleModel } from '../../../../model/store-schedule.model';
import { CentsCurrencyInputDirective } from '../../../../shared/directives/cents-currency-input.directive';
import { compressImage } from '../../../../shared/utils/image-compression.util';
import { isAllowedUploadType } from '../../../../shared/utils/file-type-validator.util';

@Component({
  selector: 'app-my-store-edit',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    TableModule,
    ImageModule,
    GoogleMapsModule,
    CalendarModule,
    ToggleButtonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextareaModule,
    InputNumberModule,
    FileUploadModule,
    ToastModule,
    CentsCurrencyInputDirective,
  ],
  templateUrl: './my-store-edit.component.html',
  styleUrl: './my-store-edit.component.css',
})
export class MyStoreEditComponent implements OnInit, AfterViewInit {
  @ViewChild(GoogleMap) googleMap!: GoogleMap;
  storeInfo: StoreModel | null = null;
  errorMessage: string | null = null;
  loading: boolean = false;
  imageLogo: File | null = null;
  logoPreview: string | null = null;

  mapCenter: google.maps.LatLngLiteral = { lat: -2.8978, lng: -79.0045 };
  mapZoom: number = 12;
  mapOptions: google.maps.MapOptions = { mapId: 'DEMO_MAP_ID' };
  private storeMarker: any = null;
  private mapInitialized: boolean = false;
  ltsSchedules: StoreScheduleModel[] = [];

  formStore = new FormGroup({
    sto_name: new FormControl('', Validators.required),
    sto_description: new FormControl(''),
    sto_phone: new FormControl('', Validators.required),
  });

  formLocation = new FormGroup({
    sto_address: new FormControl('', Validators.required),
    sto_delivery_radius_km: new FormControl(5, Validators.required),
    sto_min_order_amount: new FormControl(0, Validators.required),
    sto_lat: new FormControl<number | null>(null),
    sto_lng: new FormControl<number | null>(null),
  });

  constructor(
    private readonly apiService: CoreApiService,
    private readonly util: AppService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.getStore();
  }

  ngAfterViewInit(): void {
    // no-op: map and autocomplete initialized in getStore callback after storeInfo loads
  }

  getStore(): void {
    this.loading = true;
    this.errorMessage = null;
    this.apiService.consumirWSGet(properties.serviceMyStoreGet, null, (data) => {
      if (data.estado !== properties.codeOK || !data.data) {
        this.loading = false;
        this.errorMessage = data.mensaje || 'No se pudo cargar la información del negocio';
        this.util.mostrarToast('Editar Mi Negocio', this.errorMessage ?? '', 3);
        return;
      }
      const store: StoreModel = data.data;
      this.storeInfo = store;
      this.formStore.setValue({
        sto_name: store.sto_name,
        sto_description: store.sto_description,
        sto_phone: store.sto_phone,
      });
      this.formLocation.setValue({
        sto_address: store.sto_address,
        sto_delivery_radius_km: store.sto_delivery_radius_km,
        sto_min_order_amount: store.sto_min_order_amount,
        sto_lat: store.sto_lat,
        sto_lng: store.sto_lng,
      });

      if (store.sto_lat && store.sto_lng) {
        this.mapCenter = { lat: store.sto_lat, lng: store.sto_lng };
        this.mapZoom = 17;
      }

      this.initializeSchedules();
      this.loading = false;

      setTimeout(() => this.initMapFeatures(), 200);
    });
  }

  initializeSchedules(): void {
    this.ltsSchedules = Array.from({ length: 7 }, (_, i) => ({
      ssh_id: 0,
      ssh_day_of_week: i,
      ssh_open_time: this.stringTimeToDate('09:00') as any,
      ssh_close_time: this.stringTimeToDate('18:00') as any,
      ssh_is_open: false,
      ssh_store_id: this.storeInfo?.sto_id ?? 0,
    }));

    if (this.storeInfo?.schedules) {
      for (const existing of this.storeInfo.schedules) {
        const idx = this.ltsSchedules.findIndex(
          s => s.ssh_day_of_week === existing.ssh_day_of_week
        );
        if (idx >= 0) {
          this.ltsSchedules[idx] = {
            ...existing,
            ssh_open_time: this.stringTimeToDate(existing.ssh_open_time) as any,
            ssh_close_time: this.stringTimeToDate(existing.ssh_close_time) as any,
          };
        }
      }
    }
  }

  initMapFeatures(): void {
    this.initAutocomplete();
    const lat = this.formLocation.get('sto_lat')?.value;
    const lng = this.formLocation.get('sto_lng')?.value;
    if (lat && lng) {
      this.placeStoreMarker({ lat, lng });
    }
  }

  initAutocomplete(): void {
    const input = document.getElementById('myStoreAddressInput') as HTMLInputElement;
    if (!input) return;

    const autocomplete = new google.maps.places.Autocomplete(input, {
      fields: ['geometry', 'formatted_address'],
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.geometry?.location) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      this.formLocation.get('sto_address')?.setValue(place.formatted_address ?? '');
      this.formLocation.get('sto_lat')?.setValue(lat);
      this.formLocation.get('sto_lng')?.setValue(lng);
      this.mapCenter = { lat, lng };
      this.mapZoom = 17;

      this.placeStoreMarker({ lat, lng });
    });

    this.mapInitialized = true;
  }

  async placeStoreMarker(position: google.maps.LatLngLiteral): Promise<void> {
    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as any;
    const map = this.googleMap?.googleMap;
    if (!map) return;

    if (this.storeMarker) {
      this.storeMarker.map = null;
    }

    this.storeMarker = new AdvancedMarkerElement({
      map,
      position,
      gmpDraggable: true,
      title: 'Ubicación del negocio',
    });

    this.storeMarker.addListener('dragend', (event: any) => {
      this.formLocation.get('sto_lat')?.setValue(event.latLng.lat());
      this.formLocation.get('sto_lng')?.setValue(event.latLng.lng());
    });

    map.panTo(position);
  }

  getDayName(dayOfWeek: number): string {
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    return days[dayOfWeek];
  }

  async onUploadLogo(files: File[], fileUpload: any) {
    const doc = files[0];
    if (!isAllowedUploadType(doc, false)) {
      this.util.mostrarToast(
        'Editar Mi Negocio',
        'Formato no permitido. Solo se aceptan imágenes PNG o JPG.',
        3,
      );
      fileUpload.clear();
      return;
    }
    const file = await compressImage(doc);
    this.imageLogo = file;
    this.logoPreview = await this.previewFile(file);
    fileUpload.clear();
  }

  previewFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (reader.result) {
          resolve(reader.result as string);
        } else {
          reject('Error al leer el archivo');
        }
      };
      reader.onerror = () => reject('Error al leer el archivo');
    });
  }

  removeFile(): void {
    this.imageLogo = null;
    this.logoPreview = null;
  }

  private stringTimeToDate(timeString: string): Date {
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  private dateToStringTime(date: Date): string {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  save(): void {
    if (!this.storeInfo || this.formStore.invalid || this.formLocation.invalid) {
      return;
    }

    const schedules = this.ltsSchedules.map(schedule => ({
      ...schedule,
      ssh_open_time: this.dateToStringTime(schedule.ssh_open_time as any),
      ssh_close_time: this.dateToStringTime(schedule.ssh_close_time as any),
    }));

    const storeData: any = {
      sto_name: this.formStore.get('sto_name')?.value ?? '',
      sto_description: this.formStore.get('sto_description')?.value ?? '',
      sto_phone: this.formStore.get('sto_phone')?.value ?? '',
      sto_address: this.formLocation.get('sto_address')?.value ?? '',
      sto_delivery_radius_km:
        this.formLocation.get('sto_delivery_radius_km')?.value ?? 5,
      sto_min_order_amount:
        this.formLocation.get('sto_min_order_amount')?.value ?? 0,
      sto_lat: this.formLocation.get('sto_lat')?.value ?? null,
      sto_lng: this.formLocation.get('sto_lng')?.value ?? null,
      schedules,
    };

    const formData = new FormData();
    formData.append('datos', JSON.stringify(storeData));
    if (this.imageLogo) {
      formData.append('files', this.imageLogo, this.imageLogo.name);
    }

    this.loading = true;
    this.apiService.consumirWSFilePost(
      properties.serviceMyStoreUpdate,
      formData,
      (data) => {
        if (data.estado === properties.codeOK) {
          this.loading = false;
          this.util.mostrarToast(
            'Editar Mi Negocio',
            'El negocio ha sido actualizado',
            1,
          );
          if (this.imageLogo) {
            this.util.fetchMyLogo(this.apiService);
          }
          this.imageLogo = null;
          this.logoPreview = null;
          this.router.navigate(['/admin/my-store']);
        } else {
          this.loading = false;
          this.util.mostrarToast(
            'Editar Mi Negocio',
            data.mensaje || 'Error al actualizar el negocio',
            3,
          );
        }
      },
    );
  }

  cancel(): void {
    this.router.navigate(['/admin/my-store']);
  }
}
